import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSquareCard } from "../lib/useSquareCard";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Lock, CreditCard, ShieldCheck, ChevronLeft, HandCoins, Undo2 } from "lucide-react";
import { PARENT } from "../constants/testIds";

export default function Checkout() {
    const { regId } = useParams();
    const navigate = useNavigate();
    const [reg, setReg] = React.useState(null);
    const [config, setConfig] = React.useState(null);
    const [processing, setProcessing] = React.useState(false);

    React.useEffect(() => {
        Promise.all([api.get(`/enrollments/${regId}`), api.get("/config")])
            .then(([r, c]) => { setReg(r.data); setConfig(c.data); })
            .catch(() => toast.error("Could not load registration"));
    }, [regId]);

    // Load Square Web Payments SDK once via shared hook (StrictMode-safe).
    const { cardRef, cardReady, cardError, retry } = useSquareCard("card-container", config);

    async function payMock() {
        setProcessing(true);
        try {
            const { data } = await api.post("/payments", {
                registration_id: regId,
                source_id: "mock_token",
                idempotency_key: crypto.randomUUID(),
            });
            toast.success("Payment successful! Your children are enrolled.");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Payment failed");
        } finally { setProcessing(false); }
    }

    async function paySquare() {
        if (!cardRef.current) return toast.error("Card form is still loading, please wait a moment.");
        setProcessing(true);
        try {
            const t = await cardRef.current.tokenize();
            if (t.status !== "OK") throw new Error(t.errors?.[0]?.message || "Card verification failed");
            const { data } = await api.post("/payments", {
                registration_id: regId,
                source_id: t.token,
                idempotency_key: crypto.randomUUID(),
            });
            toast.success(data.status === "PAID" ? "Payment successful!" : "Payment submitted. We'll confirm shortly.");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Payment failed");
        } finally { setProcessing(false); }
    }

    async function requestInPerson() {
        setProcessing(true);
        try {
            await api.post(`/registrations/${regId}/request-in-person`, { note: null });
            toast.success("Great — the admin has been notified.", {
                description: "Bring your payment on Sunday and the admin will mark it paid.",
            });
            const { data } = await api.get(`/enrollments/${regId}`);
            setReg(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send request");
        } finally { setProcessing(false); }
    }

    async function cancelInPerson() {
        setProcessing(true);
        try {
            await api.post(`/registrations/${regId}/cancel-in-person`);
            toast.success("Reverted — you can pay online now.");
            const { data } = await api.get(`/enrollments/${regId}`);
            setReg(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to cancel");
        } finally { setProcessing(false); }
    }

    if (!reg || !config) return <div className="py-20 text-center text-brand-stone">Loading…</div>;
    const amount = (reg.amount_cents / 100).toFixed(2);
    const registrationFee = (config.registration_fee_cents / 100).toFixed(2);
    const monthlyFee = (config.monthly_fee_cents / 100).toFixed(2);

    return (
        <main className="py-16 min-h-[calc(100vh-5rem)]">
            <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-5 gap-10">
                <div className="md:col-span-2">
                    <button onClick={() => navigate("/dashboard")} className="text-sm text-brand-stone hover:text-primary inline-flex items-center gap-1 mb-6">
                        <ChevronLeft className="w-4 h-4" /> Back to dashboard
                    </button>
                    <div className="overline text-brand-stone mb-3">Order summary</div>
                    <Card><CardContent className="p-6">
                        <div className="text-xs text-brand-stone mb-4">First payment · $15 registration + $60 first month per child</div>
                        <div className="space-y-3">
                            {reg.students.map(s => (
                                <div key={s.id} className="flex flex-col text-sm border-b border-border/50 pb-2 last:border-0">
                                    <span className="text-brand-ink">{s.first_name} {s.last_name} · Grade {s.grade}</span>
                                    <div className="flex justify-between text-xs text-brand-stone mt-1">
                                        <span>Registration</span><span>${registrationFee}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-brand-stone">
                                        <span>First month tuition</span><span>${monthlyFee}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-border/60 flex justify-between items-baseline">
                            <span className="font-dash text-lg">Total today</span>
                            <span className="font-serif text-3xl text-brand-ink">${amount}</span>
                        </div>
                        <div className="mt-4 p-3 rounded-lg bg-secondary/60 text-xs text-brand-stone leading-relaxed">
                            After today, monthly tuition of <strong className="text-brand-ink">${monthlyFee}/month</strong> per child begins in 30 days and continues for the school year.
                        </div>
                        <Badge className={`mt-4 ${reg.status === "PAID" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0`}>{reg.status}</Badge>
                    </CardContent></Card>
                    <div className="mt-6 text-xs text-brand-stone flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5" /> Payments are securely processed by Square.
                    </div>
                </div>

                <div className="md:col-span-3">
                    <Card>
                        <CardContent className="p-8">
                            <div className="flex items-center gap-2 mb-6">
                                <Lock className="w-4 h-4 text-primary" />
                                <div className="overline text-brand-stone">Secure payment</div>
                            </div>
                            <h2 className="font-serif text-2xl text-brand-ink">Pay with card</h2>
                            {reg.status === "PAID" ? (
                                <div className="mt-8 p-6 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800">
                                    This invoice has already been paid. Alhamdulillah!
                                </div>
                            ) : reg.in_person_requested ? (
                                <div className="mt-6 p-5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 space-y-3" data-testid="in-person-requested-card">
                                    <div className="flex items-start gap-2">
                                        <HandCoins className="w-5 h-5 mt-0.5 shrink-0" />
                                        <div>
                                            <div className="font-dash text-lg font-semibold">Pay in person selected</div>
                                            <div className="text-sm">The admin has been notified. Please bring your payment on Sunday — they&apos;ll mark it paid then.</div>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={cancelInPerson}
                                        disabled={processing}
                                        data-testid="cancel-in-person-btn"
                                        className="gap-2"
                                    >
                                        <Undo2 className="w-3.5 h-3.5" /> Change my mind — pay online
                                    </Button>
                                </div>
                            ) : config.square_mock_mode ? (
                                <>
                                    <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                                        <div className="font-semibold">Sandbox demo mode</div>
                                        <div className="mt-1">Square credentials aren&apos;t configured yet — clicking below will simulate a successful payment so the admin can plug in real Square keys later.</div>
                                    </div>
                                    <Button size="lg" onClick={payMock} disabled={processing}
                                        data-testid={PARENT.payBtn}
                                        className="mt-6 w-full bg-primary hover:bg-primary/90 h-12 gap-2">
                                        <CreditCard className="w-4 h-4" /> {processing ? "Processing…" : `Simulate payment of $${amount}`}
                                    </Button>
                                    <div className="my-4 flex items-center gap-3 text-[11px] text-brand-stone uppercase tracking-wider">
                                        <div className="h-px flex-1 bg-border" />
                                        <span>or</span>
                                        <div className="h-px flex-1 bg-border" />
                                    </div>
                                    <Button size="lg" variant="outline" onClick={requestInPerson} disabled={processing}
                                        data-testid="pay-in-person-btn"
                                        className="w-full h-12 gap-2">
                                        <HandCoins className="w-4 h-4" /> Pay in person on Sunday
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <div className="mt-6 relative">
                                        <div id="card-container" className="min-h-[80px]" />
                                        {!cardReady && !cardError && (
                                            <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                                <div className="text-sm text-brand-stone animate-pulse">Loading secure card form…</div>
                                            </div>
                                        )}
                                        {cardError && (
                                            <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-center justify-between gap-3">
                                                <span>{cardError}. Please try again.</span>
                                                <Button size="sm" variant="outline" onClick={retry} data-testid="retry-card-btn">Retry</Button>
                                            </div>
                                        )}
                                    </div>
                                    <Button size="lg" onClick={paySquare} disabled={processing || !cardReady}
                                        data-testid={PARENT.payBtn}
                                        className="mt-6 w-full bg-primary hover:bg-primary/90 h-12 gap-2">
                                        <CreditCard className="w-4 h-4" /> {processing ? "Processing…" : `Pay $${amount}`}
                                    </Button>
                                    <div className="my-4 flex items-center gap-3 text-[11px] text-brand-stone uppercase tracking-wider">
                                        <div className="h-px flex-1 bg-border" />
                                        <span>or</span>
                                        <div className="h-px flex-1 bg-border" />
                                    </div>
                                    <Button size="lg" variant="outline" onClick={requestInPerson} disabled={processing}
                                        data-testid="pay-in-person-btn"
                                        className="w-full h-12 gap-2">
                                        <HandCoins className="w-4 h-4" /> Pay in person on Sunday
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </main>
    );
}
